import type { ReactNode } from 'react';
import { Linking, StyleSheet, Text, View } from 'react-native';
import Head from 'expo-router/head';

import { Screen } from '~/components/Screen';
import { TopNav } from '~/components/TopNav';
import { colors, fontFamily, space, spacing, type } from '~/ui/tokens';

const SUPPORT_EMAIL = 'customercare@suitescanner.com';
const GRIEVANCE_EMAIL = 'dd@suitescanner.com';

function Section({ title, children }: { title: string; children: ReactNode }) {
    return (
        <View style={styles.section}>
            <Text style={styles.sectionTitle}>{title}</Text>
            {children}
        </View>
    );
}

function P({ children }: { children: ReactNode }) {
    return <Text style={styles.paragraph}>{children}</Text>;
}

function Bullet({ children }: { children: ReactNode }) {
    return (
        <View style={styles.bulletRow}>
            <Text style={styles.bulletDot}>{'•'}</Text>
            <Text style={styles.bulletText}>{children}</Text>
        </View>
    );
}

function EmailLink({ email }: { email: string }) {
    return (
        <Text style={styles.link} accessibilityRole="link" onPress={() => Linking.openURL(`mailto:${email}`)}>
            {email}
        </Text>
    );
}

function PrivacyScreen() {
    return (
        <Screen>
            <Head>
                <title>Privacy Policy — SuiteScanner</title>
                <meta name="description" content="How SuiteScanner collects, uses, and protects your personal information when you search for and book a hotel." />
            </Head>
            <TopNav back />
            <View style={styles.body}>
                <Text style={styles.heading}>Privacy Policy</Text>
                <Text style={styles.updated}>Last updated September 2026</Text>
                <P>
                    At Onhouse Technologies Pvt Ltd (CIN U62099DL2025PTC454572), operator of SuiteScanner, we value
                    your trust and are committed to protecting your personal information. This Privacy Policy,
                    published in accordance with the Information Technology Act, 2000, the Information Technology
                    (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules,
                    2011, and the Digital Personal Data Protection Act, 2023 (DPDP Act), explains how we collect,
                    use, and safeguard your data when you use our services.
                </P>

                <Section title="1. Information We Collect">
                    <Text style={styles.subTitle}>1.1 Information You Provide</Text>
                    <Bullet>Contact information (name, email, phone number)</Bullet>
                    <Bullet>Travel and stay preferences and requirements</Bullet>
                    <Bullet>Guest details (names, ages, and ID/passport information where required for a booking)</Bullet>
                    <Bullet>Payment information (processed securely through our payment partners)</Bullet>
                    <Bullet>Communication history with our team, including email and support chat</Bullet>
                    <Text style={styles.subTitle}>1.2 Automatically Collected Information</Text>
                    <Bullet>Device information and browser type</Bullet>
                    <Bullet>IP address and approximate location</Bullet>
                    <Bullet>Pages visited and time spent on our site</Bullet>
                    <Bullet>Referral source</Bullet>
                </Section>

                <Section title="2. How We Use Your Information">
                    <P>We use your information to:</P>
                    <Bullet>Process and manage your hotel bookings</Bullet>
                    <Bullet>Provide personalized hotel search results and recommendations</Bullet>
                    <Bullet>Communicate booking confirmations and updates</Bullet>
                    <Bullet>Send relevant offers and travel inspiration, with your consent</Bullet>
                    <Bullet>Improve our services and website experience</Bullet>
                    <Bullet>Comply with legal requirements</Bullet>
                </Section>

                <Section title="3. Information Sharing">
                    <P>We share your information only when necessary:</P>
                    <Bullet>Service Providers: hotels and accommodation providers need your details to confirm bookings</Bullet>
                    <Bullet>Payment Processors: secure payment partners process your transactions — we do not store your card details</Bullet>
                    <Bullet>Legal Requirements: when required by law or to protect our legal rights</Bullet>
                    <P>We do not sell your personal information to third parties.</P>
                </Section>

                <Section title="4. Data Security">
                    <P>
                        We implement appropriate technical and organizational measures to protect your personal
                        information against unauthorized access, alteration, disclosure, or destruction. This
                        includes encrypted data transmission, secure servers, and access controls.
                    </P>
                </Section>

                <View nativeID="cookies"><Section title="5. Cookies">
                    <P>
                        Our website uses cookies to enhance your browsing experience and analyze site traffic. You
                        can control cookie settings through your browser preferences. Essential cookies required for
                        basic functionality cannot be disabled.
                    </P>
                </Section></View>

                <Section title="6. Children's Data">
                    <P>
                        Bookings often include family members, and we may collect personal data of children (such as
                        names, ages, and travel document details) for the purpose of processing a booking. In
                        accordance with the DPDP Act, we process children's data only with the consent of the parent
                        or legal guardian making the booking. We do not knowingly collect data directly from
                        children.
                    </P>
                </Section>

                <Section title="7. Your Rights">
                    <P>You have the right to:</P>
                    <Bullet>Access the personal information we hold about you</Bullet>
                    <Bullet>Request correction of inaccurate information</Bullet>
                    <Bullet>Request deletion of your data, subject to legal retention requirements</Bullet>
                    <Bullet>Opt out of marketing communications at any time</Bullet>
                    <Bullet>Withdraw consent for data processing</Bullet>
                </Section>

                <Section title="8. Data Retention">
                    <P>
                        We retain your personal information for as long as necessary to provide our services and
                        comply with legal obligations. We periodically review and delete data that is no longer
                        required. Booking records may be retained as required for tax and legal compliance purposes
                        under applicable Indian law.
                    </P>
                </Section>

                <Section title="9. Third-Party Links">
                    <P>
                        Our website may contain links to third-party websites. We are not responsible for the
                        privacy practices of these external sites. We encourage you to review their privacy
                        policies.
                    </P>
                </Section>

                <Section title="10. Updates to This Policy">
                    <P>
                        We may update this Privacy Policy from time to time. Significant changes will be
                        communicated through our website or email. Continued use of our services after updates
                        constitutes acceptance of the revised policy.
                    </P>
                </Section>

                <Section title="11. Grievance Officer">
                    <P>
                        In accordance with the Information Technology Act, 2000, the DPDP Act, 2023, and the rules
                        made thereunder, the Grievance Officer for the purposes of this Privacy Policy is:
                    </P>
                    <P>
                        Grievance Officer, Onhouse Technologies Pvt Ltd{'\n'}
                        Email: <EmailLink email={GRIEVANCE_EMAIL} />{'\n'}
                        C-8/8522, Vasant Kunj, New Delhi - 110070, India
                    </P>
                    <P>
                        We will acknowledge your grievance within 24 hours and endeavour to resolve it within 15
                        days from the date of receipt. If you are unsatisfied with our resolution, you may approach
                        the Data Protection Board of India under the DPDP Act.
                    </P>
                </Section>

                <Section title="12. Contact Us">
                    <P>
                        For privacy-related questions or to exercise your rights, contact us at{' '}
                        <EmailLink email={SUPPORT_EMAIL} />.
                    </P>
                </Section>
            </View>
        </Screen>
    );
}

const styles = StyleSheet.create({
    body: { maxWidth: 720, width: '100%', alignSelf: 'center', paddingHorizontal: space.card, paddingTop: space.cardLarge, paddingBottom: space.sectionMajor, gap: space.related },
    heading: { fontFamily: fontFamily.bold, ...type.pageTitle, color: colors.ink },
    updated: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint, marginBottom: space.related },
    section: { marginTop: space.card, gap: space.related },
    sectionTitle: { fontFamily: fontFamily.semibold, ...type.subsectionTitle, color: colors.ink },
    subTitle: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.ink, marginTop: space.micro },
    paragraph: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
    bulletRow: { flexDirection: 'row', gap: space.related, paddingLeft: spacing(0.5) },
    bulletDot: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
    bulletText: { flex: 1, fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
    link: { fontFamily: fontFamily.semibold, color: colors.brand },
});

export default PrivacyScreen;
